/*───────────────────────────────────────────────────────────────
 🌐 Project: Ring2All Platform
 🧑‍💻 Author: Rodrigo Cuadra
 🏢 Company: Ring2All LLC
 📄 File: utils/settingsHelpers.ts (Ring Groups)
 🗓️ Created: 2025-01-15
 🧩 Description:
    Helper utilities for managing Ring Group settings.

────────────────────────────────────────────────────────────────*/

import { FormState, ToggleOption, ForwardOption, MissedCallAction } from '../types';
import { KNOWN_SETTINGS_KEYS } from '../constants';

/**
 * Build settings object from form state
 */
export function buildSettingsFromFormState(formState: FormState): Record<string, unknown> {
  const settings: Record<string, unknown> = {};

  if (formState.extension) {
    settings.extension = formState.extension;
  }

  if (formState.greeting) {
    settings.greeting = formState.greeting;
  }

  if (formState.callerIdName) {
    settings.cid_name_prefix = formState.callerIdName;
  }

  if (formState.callerIdNumber) {
    settings.cid_number_prefix = formState.callerIdNumber;
  }

  if (formState.distinctiveRing) {
    settings.distinctive_ring = formState.distinctiveRing;
  }

  settings.ringback = formState.ringback || '${us-ring}';
  settings.ringback_type = 'local_stream://default';
  settings.call_screen = formState.callScreen;
  settings.call_forward = formState.callForward;
  settings.follow_me = formState.followMe;
  settings.missed_call_action = formState.missedCallAction;
  settings.forward_enabled = formState.forwardEnabled;

  if (formState.forwardEnabled === 'number' && formState.forwardNumber) {
    settings.forward_number = formState.forwardNumber;
  }

  if (formState.forwardTollAllow) {
    settings.forward_toll_allow = formState.forwardTollAllow;
  }

  settings.confirm_key = formState.confirmKey || '1';
  settings.confirm_file = formState.confirmFile || 'ivr/press_1_to_accept.wav';

  if (formState.presenceId) {
    settings.presence_id = formState.presenceId;
  }

  settings.record = formState.record;

  if (formState.callTimeoutOverride) {
    const timeout = Number(formState.callTimeoutOverride);
    if (Number.isFinite(timeout) && timeout > 0) {
      settings.call_timeout_override = timeout;
    }
  }

  return settings;
}

/**
 * Extract extra settings (unknown keys) from settings object
 */
export function extractExtraSettings(settings: Record<string, unknown>): Record<string, unknown> {
  const extraSettings: Record<string, unknown> = {};
  Object.entries(settings).forEach(([key, value]) => {
    if (!KNOWN_SETTINGS_KEYS.has(key)) {
      extraSettings[key] = value;
    }
  });
  return extraSettings;
}

/**
 * Get string setting with fallback
 */
export function getStringSetting(settings: Record<string, unknown>, key: string, fallback = ''): string {
  const value = settings[key];
  return typeof value === 'string' ? value : fallback;
}

/**
 * Convert value to ToggleOption
 */
export function asToggle(value: unknown, fallback: ToggleOption): ToggleOption {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'enabled' || normalized === 'disabled') {
      return normalized;
    }
  }
  return fallback;
}

/**
 * Convert value to ForwardOption
 */
export function asForwardOption(value: unknown, fallback: ForwardOption): ForwardOption {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'disabled' || normalized === 'number') {
      return normalized;
    }
  }
  return fallback;
}

/**
 * Convert value to MissedCallAction
 */
export function asMissedCall(value: unknown, fallback: MissedCallAction): MissedCallAction {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'none' || normalized === 'email' || normalized === 'sms' || normalized === 'internal') {
      return normalized;
    }
  }
  return fallback;
}

